import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/es";
import axios from "../../api/axios";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

dayjs.locale("es");

function toDayjsSafe(raw) {
  if (!raw) return null;
  const d = dayjs(raw);
  return d.isValid() ? d : null;
}

function intersectIntervals(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return null;
  const start = aStart.isAfter(bStart) ? aStart : bStart;
  const end = aEnd.isBefore(bEnd) ? aEnd : bEnd;
  if (end.isBefore(start)) return null;
  return { start, end };
}


function buildIntervalFromItem(item, periodoIni, periodoFin) {
  const tipo = item.tipo_contingencia;

  if (tipo === "ESPECIFICA" || tipo === "ZONAL") {
    const start = toDayjsSafe(item.especifica_fecha_hora_ocurrencia);
    const end = toDayjsSafe(item.especifica_fecha_hora_resolucion);
    if (!start || !end || !start.isValid() || !end.isValid()) return null;
    if (end.isBefore(start)) return null;
    return { start, end, raw: item };
  }

  if (tipo === "PROGRAMADA") {
    const dIni = item.programada_inicio_contingencia;
    const dFin = item.programada_fin_contingencia;
    if (!dIni || !dFin) return null;

    const tIni = item.programada_hora_inicio || "00:00:00";
    const tFin = item.programada_hora_termino || "23:59:59";

    const start = toDayjsSafe(`${dIni}T${tIni}`);
    const end = toDayjsSafe(`${dFin}T${tFin}`);
    if (!start || !end || !start.isValid() || !end.isValid()) return null;
    if (end.isBefore(start)) return null;

    if (periodoIni && periodoFin) {
      const clipped = intersectIntervals(start, end, periodoIni, periodoFin);
      if (!clipped) return null;
      return { ...clipped, raw: item };
    }

    return { start, end, raw: item };
  }

  return null;
}

function mergeIntervals(intervals) {
  if (!intervals.length) return [];
  const sorted = [...intervals].sort(
    (a, b) => a.start.valueOf() - b.start.valueOf()
  );

  const result = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const prev = result[result.length - 1];
    const curr = sorted[i];

    if (curr.start.isBefore(prev.end) || curr.start.isSame(prev.end)) {
      if (curr.end.isAfter(prev.end)) {
        prev.end = curr.end;
      }
    } else {
      result.push(curr);
    }
  }
  return result;
}


const PeriodosGraciaAlert = ({ idOperador, periodoActual, onRangesChange }) => {
  const [intervalos, setIntervalos] = useState([]);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // "primer día del mes" del período actual en formato "YYYY-MM-01"
  const periodoStr = useMemo(() => {
    if (periodoActual) {
      if (dayjs.isDayjs(periodoActual)) {
        return periodoActual.startOf("month").format("YYYY-MM-01");
      }
      if (typeof periodoActual === "string" && periodoActual.length >= 10) {
        const d = dayjs(periodoActual.substring(0, 10));
        if (d.isValid()) {
          return d.startOf("month").format("YYYY-MM-01");
        }
      }
    }
    return dayjs().startOf("month").format("YYYY-MM-01");
  }, [periodoActual]);

  useEffect(() => {
    if (!idOperador) {
      setIntervalos([]);
      setPayload(null);
      if (typeof onRangesChange === "function") onRangesChange([]);
      return;
    }

    let cancelado = false;
    setLoading(true);
    setErrorMsg("");

    const processResponse = (res) => {
      const data = res.data || {};
      const items = Array.isArray(data.items) ? data.items : [];

      const periodoIni = toDayjsSafe(data.periodo_ini);
      const periodoFin = toDayjsSafe(data.periodo_fin);

      const intervalosCrudos = items
        .map((it) => buildIntervalFromItem(it, periodoIni, periodoFin))
        .filter(Boolean);

      const intervalosFusionados = mergeIntervals(intervalosCrudos);

      setPayload(data);
      setIntervalos(intervalosFusionados);

      const rangesFormateados = intervalosFusionados.map((r) => ({
        inicio: r.start.format("YYYY-MM-DD HH:mm"),
        fin: r.end.format("YYYY-MM-DD HH:mm"),
      }));

      if (typeof onRangesChange === "function") {
        onRangesChange(rangesFormateados);
      }
    };

    const fetchData = async () => {
      try {
        const res = await axios.getPeriodosGracia(idOperador, periodoStr);
        if (cancelado) return;
        processResponse(res);
      } catch (err) {
        if (cancelado) return;
        console.error("Error al obtener periodos de gracia (intento 1):", err);

        try {
          const res2 = await axios.getPeriodosGracia(idOperador, periodoStr);
          if (cancelado) return;
          processResponse(res2);
        } catch (err2) {
          if (cancelado) return;
          console.error("Error al obtener periodos de gracia (intento 2):", err2);
          setErrorMsg("No se pudo cargar la información de periodos de gracia.");
          setPayload(null);
          setIntervalos([]);
          if (typeof onRangesChange === "function") {
            onRangesChange([]);
          }
        }
      } finally {
        if (!cancelado) setLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelado = true;
    };
  }, [idOperador, periodoStr]);

  if (!idOperador) return null;

  if (loading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="info" variant="outlined">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              Cargando períodos sin descuento para este mes...
            </Typography>
          </Box>
        </Alert>
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="error" variant="filled">
          {errorMsg}
        </Alert>
      </Box>
    );
  }

  if (!intervalos.length) {
    return null;
  }

  const periodoIni = toDayjsSafe(payload?.periodo_ini);
  const periodoFin = toDayjsSafe(payload?.periodo_fin);

  return (
    <Box sx={{ mt: 2 }}>
      <Alert
        severity="warning"
        variant="filled"
        icon={<WarningAmberIcon sx={{ fontSize: 36 }} />}
        sx={{
          backgroundColor: "#FFB39D",
          color: "#4A1F00",
          "& .MuiAlert-icon": {
            alignItems: "center",
            mr: 2,
          },
          "& .MuiAlert-message": {
            fontSize: "0.95rem",
          },
        }}
      >
        <AlertTitle sx={{ fontWeight: "bold" }}>
          Atención: existen períodos no afectos a descuento
        </AlertTitle>

        {payload && periodoIni && periodoFin && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Se informa a los operadores que no se aplicarán descuentos durante el período comprendido entre:
          </Typography>
        )}

        <Box component="ul" sx={{ pl: 2, mb: 0 }}>
          {intervalos.map((r, idx) => (
            <li key={idx}>
              <Typography variant="body2">
                <strong>{r.start.format("YYYY-MM-DD HH:mm")} y el {" "}
                {r.end.format("YYYY-MM-DD HH:mm")}:</strong> Este intervalo corresponde a período de vacaciones, por lo que no se aplicarán descuentos de ningún tipo, y el servicio será pagado en forma íntegra.)
              </Typography>
            </li>
          ))}
        </Box>
         <Typography variant="body2" sx={{ mt: 1 }}>
          En consecuencia, la plataforma no permitirá la creación ni el registro de contingencias cuya vigencia se encuentre completamente dentro del período señalado.
        </Typography>



        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>El servicio deberá seguir siendo prestado con normalidad a todos los establecimientos.</strong>
        </Typography>
      </Alert>
    </Box>
  );
};



export default PeriodosGraciaAlert;
